import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { Routes } from '../../../../config/routes';
import environment from '../../../../../environments/environment';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { OrganizationFile } from '../../../../models/entities/organization-file';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { Transaction } from '../../../../models/entities/transactions/transaction';
import { TransactionsService } from '../../../../services/accounting/transactions.service';
import { ViolationStatement } from '../../../../models/entities/violation-statement';
import { TransactionAttachment } from 'app/models/entities/transaction-attachment';
import { BillPayment } from 'app/models/entities/bill-payment';
import { UploadedFile } from 'app/models/entities/uploaded-file';
import { UploadedFilesService } from 'app/services/uploaded-files.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { GrowlerService } from '../../../../services/growler.service';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { FileUploaderService } from 'app/services/file-uploader.service';
import { FormSubmission } from '../../../../models/entities/forms/form-submission';
import { Ballot } from '../../../../models/entities/voting/ballot';
import { BallotQuestion } from '../../../../models/entities/voting/ballot-question';

export interface MultiDocumentUploaderDialogInput
{
    type: string;
    transaction?: Transaction;
    violation?: ViolationStatement;
    vendor?: Vendor;
    billPayment?: BillPayment;
    ballot?: Ballot;
    ballotQuestion?: BallotQuestion;
    formSubmission?: FormSubmission;
    hideUploader?: boolean;
    uploadedFiles?: UploadedFile[];
    transactionAttachments?: TransactionAttachment[];
    newStyles?: boolean;
}

@Component({
    selector: 'app-multi-document-uploader',
    styleUrls: ['./multi-document-uploader-dialog.component.scss'],
    templateUrl: './multi-document-uploader-dialog.component.html',
})
export class MultiDocumentUploaderDialogComponent implements OnInit
{
    @Output() uploadComplete: EventEmitter<void> = new EventEmitter<void>();

    title = 'Upload Documents';
    fileItem = null;
    uploadedFiles: UploadedFile[] = [];
    transactionAttachments: TransactionAttachment[] = [];
    displayType = 'transaction';

    attachmentCount = 0;

    isOverDropZone = false;
    isAttaching = false;
    isDeletingAttachment = false;
    jwt: JwtLegFiClaims;

    fileUploader: FileUploader;
    filesMapping: { [prop: string]: string; } = {
        '=0': 'Add Files',
        '=1': 'Add File',
        'other': 'Add # Files',
    };

    constructor(
            private _dialogRef: MatDialogRef<MultiDocumentUploaderDialogComponent>,
            private _growler: GrowlerService,
            private _transactionsService: TransactionsService,
            private _uploadedFilesService: UploadedFilesService,
            private _fileUploaderService: FileUploaderService,
            @Inject(MAT_DIALOG_DATA) public data: MultiDocumentUploaderDialogInput,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        this.title = 'Upload Documents';
        this.uploadedFiles = this.data.uploadedFiles || [];

        if (this.jwt) {
            this.prepFileUploader();
        }

        if (this.data.transaction?.attachments?.length || this.data.billPayment?.attachments?.length) {
            this.title = 'Viewing Attachments';
        }
        if (this.data?.transaction) {
            this.title += ' for ' + this.data?.transaction?.description;
        }
    }

    deleteUploadedFile(uploadedFile) {
        this._uploadedFilesService.unlink(uploadedFile, this.data.type, this.data[this.data.type].id).subscribe(
                () => {
                    this._growler.success('Success', `The selected file has been removed.`);
                    const indexOfSelected = this.uploadedFiles.indexOf(uploadedFile);
                    this.uploadedFiles.splice(indexOfSelected, 1);
                },
        );
    }

    openAttachment(url: string) {
        window.open(url, '_blank');
    }

    prepFileUploader() {
        switch (this.data.type) {
            case 'transaction':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(
                            Routes.LegFiCore.TransactionAttachments(this.jwt.orgId, this.data.transaction.id),
                    ),
                });
                break;
            case 'formSubmission':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                    additionalParameter: {
                        linked_with_type: 'submission',
                        linked_with_id: this.data.formSubmission.id,
                    },
                });
                break;
            case 'violation':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                    additionalParameter: {
                        linked_with_type: 'violation',
                        linked_with_id: this.data.violation.id,
                    },
                });
                break;
            case 'vendor':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.BillPayVendorAttachments(this.jwt.orgId, this.data.vendor.id)),
                });
                break;
            case 'billPayment':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                    additionalParameter: {
                        linked_with_type: 'billPayment',
                        linked_with_id: this.data.billPayment.id,
                    },
                });
                this.displayType = 'bill payment';
                break;
            case 'ballot':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                    additionalParameter: {
                        linked_with_type: 'ballot',
                        linked_with_id: this.data.ballot.id,
                    },
                });
                this.displayType = 'ballot';
                break;
            case 'ballotQuestion':
                this.fileUploader = this._fileUploaderService.initUploader({
                    url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                    additionalParameter: {
                        linked_with_type: 'ballotQuestion',
                        linked_with_id: this.data.ballotQuestion.id,
                    },
                });
                this.displayType = 'ballotQuestion';
                break;
        }

        this.fileUploader.options.autoUpload = false;

        this.fileUploader.onAfterAddingFile = (file): any => {
            if (file.file.size > environment.WorkerServerFileSizeLimit) {
                this._growler.oops('One of your files exceeds the 100 MB limit.');
            }
            file.file.size = OrganizationFile.convertFileSizeToHumanReadable(file.file.size, 0);
            this.fileItem = file;
        };

        this.fileUploader.onCompleteAll = (): any => {
            this._growler.success('Success', 'Attachments added');
            this.fileUploader.clearQueue();

            this._dialogRef.close(<GenericDialogResult>{
                success: true,
            });
        };
    }

    processFiles() {
        this.attachmentCount = 0;
        this.isAttaching = true;

        this.fileUploader.uploadAll();
    }

    deleteAttachment(a) {
        const attachmentToDelete = a;
        this.isDeletingAttachment = true;

        // TODO - figure out if this should handle other types?
        this._transactionsService.deleteAttachment(this.data.transaction.id, attachmentToDelete.id).subscribe(() => {
            this.data.transaction.attachments = this.data.transaction.attachments.filter((attachment) => {
                if (attachmentToDelete.id !== attachment.id) {
                    return attachment;
                }
            });

            this.isDeletingAttachment = false;
            this._growler.success('Deleted', 'Attachment has been deleted');
        });
    }

    fileOverDropzone(e): void {
        this.isOverDropZone = e;
    }
}
